import {
  EntityId,
  EntitySelectEntity,
  EntitySelectSubEntity,
} from '@factorialco/f0-react/dist/experimental'
import { AvatarNameForSelectFragment } from 'generated/resources/graphql'
import { orderBy } from 'lodash'

import { Employee } from 'modules/core/hooks/useCurrentSession'
import buildUrl from 'modules/core/lib/buildUrl'
import { DisplayNameStrategy, resolveEmployeeDisplayName } from 'modules/core/lib/employeeName'
import i18n from 'modules/core/lib/i18n'

export function groupByManagers(
  employees?: AvatarNameForSelectFragment[],
  excludedIds: EntityId[] = [],
  expandedElements: EntityId[] = [],
  self?: Employee,
  displayNameStrategy: DisplayNameStrategy = 'preferredOrFull'
): EntitySelectEntity[] {
  if (!employees) return []

  // When we're looking for reportees, us as an entity is not being returned, so we need to add it manually
  const isSelf = (id: string) => {
    return !excludedIds.includes(String(self?.id)) && id === String(self?.id)
  }

  const findManager = (id: string) => {
    const manager = employees.find((value) => value.id === id)

    return manager
  }

  const managerMap = new Map<string, EntitySelectEntity>()

  employees.forEach((employee) => {
    const { id, fullName, avatar, firstName, lastName, preferredName, managerId } = employee

    const managerIdKey = managerId ? `-${managerId}` : '0'

    let managerEntity = managerMap.get(managerIdKey)

    if (!managerEntity) {
      const name = isSelf(managerId ?? '')
        ? resolveEmployeeDisplayName({ fullName, lastName, preferredName }, displayNameStrategy)
        : (managerId ? findManager(managerId)?.fullName : null) ??
          i18n.t('employee_selector.groups.managers.no_manager')

      managerMap.set(managerIdKey, {
        id: managerIdKey,
        name: name ?? '',
        expanded: expandedElements.includes(managerIdKey),
        subItems: [],
      })

      managerEntity = managerMap.get(managerIdKey)
    }

    const subItem: EntitySelectSubEntity = {
      subId: id,
      subName: fullName,
      subAvatar: avatar ? buildUrl(avatar.url) : undefined,
      subSearchKeys: [
        preferredName ? `${preferredName} ${lastName}` : null,
        `${firstName} ${lastName}`,
      ].filter(Boolean),
    }

    if (!excludedIds.includes(id)) {
      managerEntity?.subItems?.push(subItem)
    }
  })

  const sortedManagers = orderBy(
    Array.from(managerMap.values()),
    [
      (manager) => manager.id !== '0', // No manager is first
      'name',
    ],
    ['asc', 'asc']
  )

  return Array.from(sortedManagers)
}
